import { api, Betriebsstoerung } from './api'
import { meldeErfolg } from './Erfolgsstelle'
import { meldeStoerung } from './Stoerungsstelle'
import { t } from './sprache'
import type { Rechenstand } from './typen'

/**
 * **Konzernabschlüsse, die im Hintergrund rechnen** (7.2 Abschnitt 40,
 * R1252–R1254) — eine Stelle für die ganze Anwendung, ohne Bildschirmteil.
 *
 * Bis zum 2026-10-06 hielt der Anstoß die Seite fest, bis alle zwölf Monate
 * gerechnet und gespeichert waren, und sagte bis dahin nichts. Jetzt kommt die
 * Antwort sofort; von hier aus wird nachgefragt, wie weit er ist. Die Seite
 * der Konzernabschlüsse liest hier ihren Balken, und **wer sie verlassen hat,
 * bekommt das Ende im Kästchen oben rechts** (R1253) — weil diese Stelle nicht
 * an der Seite hängt, sondern an der Anwendung.
 */

/** Wie oft nachgefragt wird, solange etwas rechnet. */
export const NACHFRAGE_MS = 700

/** Ein eigener Lauf, der zu Ende ist und auf der Seite noch etwas zu sagen hat. */
export type Ergebnis = {
  stand: Rechenstand
  name: string
}

type Bild = {
  staende: Record<string, Rechenstand>
  /** Was die Seite zeigen soll: Bericht mit Befund oder fachlicher Abbruch. */
  ergebnisse: Ergebnis[]
}

let bild: Bild = { staende: {}, ergebnisse: [] }
const hoerer = new Set<() => void>()

/** Was in diesem Browser angestoßen wurde — mit dem Namen fürs Kästchen. */
const eigene = new Map<string, string>()
let seiten = 0
let uhr: number | null = null

function aendern(neu: Bild) {
  bild = neu
  for (const h of hoerer) {
    h()
  }
}

export function rechenlaeufe(): Bild {
  return bild
}

export function abonniereRechenlaeufe(h: () => void): () => void {
  hoerer.add(h)
  return () => {
    hoerer.delete(h)
  }
}

/** Ob noch etwas rechnet, worauf jemand wartet. */
function wartet(): boolean {
  const laufende = Object.values(bild.staende).filter((s) => s.laeuft)
  return laufende.some((s) => eigene.has(s.konzernabschluss)) || (seiten > 0 && laufende.length > 0)
}

function planeNachfrage() {
  if (uhr !== null || !wartet()) {
    return
  }
  uhr = window.setTimeout(() => {
    uhr = null
    void frageNach()
  }, NACHFRAGE_MS)
}

/** Holt alle Stände und meldet, was seit der letzten Frage zu Ende ging. */
export async function frageNach(): Promise<void> {
  let neu: Rechenstand[]
  try {
    neu = await api.konzernabschlusslaeufe()
  } catch {
    // Eine verpasste Nachfrage ist keine Störung: Die nächste kommt gleich.
    planeNachfrage()
    return
  }

  const vorher = bild.staende
  const staende: Record<string, Rechenstand> = {}
  for (const s of neu) {
    staende[s.konzernabschluss] = s
  }
  // **Ein eigener Lauf, der ohne Ende verschwindet, sagt es** (7.2 Abschnitt
  // 40.5, R1257): Der Dienst wurde neu gestartet. Gespeichert ist nichts
  // (R1256) — und ohne diesen Satz sähe die Zeile aus wie gerechnet.
  for (const [schluessel, name] of [...eigene]) {
    if (!staende[schluessel]) {
      eigene.delete(schluessel)
      meldeErfolg(t('konzernabschluss.verschwunden', { name }), [
        {
          satz: t('konzernabschluss.verschwunden.satz'),
          knopf: t('konzernabschluss.neurechnen'),
          aufKnopf: () => {
            // Eine Störung beim erneuten Anstoß meldet die Abrufstelle selbst.
            stosseAn(schluessel, name).catch(() => {})
          },
        },
      ])
    }
  }

  const ergebnisse = [...bild.ergebnisse]
  for (const s of neu) {
    if (!s.laeuft && vorher[s.konzernabschluss]?.laeuft && eigene.has(s.konzernabschluss)) {
      const name = eigene.get(s.konzernabschluss) ?? s.konzernabschluss
      eigene.delete(s.konzernabschluss)
      if (beende(s, name)) {
        ergebnisse.push({ stand: s, name })
      }
    }
  }

  aendern({ staende, ergebnisse })
  planeNachfrage()
}

/**
 * Das Ende eines eigenen Laufs (R1253). Gibt zurück, ob die Seite noch etwas
 * dazu zeigen soll.
 */
function beende(s: Rechenstand, name: string): boolean {
  if (s.abbruch?.kennung) {
    // Technisch: das Störungsfenster, wie bei jeder Anfrage (R1003, R1005).
    meldeStoerung(new Betriebsstoerung('meldung.stoerung.abgebrochen', { kennung: s.abbruch.kennung }))
    return false
  }

  const gespeichert = (s.bericht?.gespeichert ?? 0) > 0
  if (gespeichert) {
    meldeErfolg(t('erfolg.konzernabschluss.gerechnet'))
  } else if (seiten === 0) {
    // Nicht gespeichert, und die Seite ist zu: Das Kästchen bleibt und führt hin.
    meldeErfolg(t('konzernabschluss.nichtgespeichert', { name }), [
      {
        satz: t(s.abbruch ? 'konzernabschluss.nichtgespeichert.abbruch' : 'konzernabschluss.nichtgespeichert.monate'),
        knopf: t('konzernabschluss.ansehen'),
        aufKnopf: () => {
          window.location.hash = '#/abschluss/konzernabschluesse'
        },
      },
    ])
  }

  return !gespeichert || (s.bericht?.befunde.length ?? 0) > 0
}

/** Stößt an — die Antwort kommt sofort, der Balken folgt (R1252). */
export async function stosseAn(schluessel: string, name: string): Promise<void> {
  // Rechnete er schon für jemand anderen, ist es dessen Lauf (R1254), und
  // dessen Ende meldet das Kästchen nicht hier.
  const fremd = bild.staende[schluessel]?.laeuft === true && !eigene.has(schluessel)
  const s = await api.konzernabschlussAnstossen(schluessel)
  if (!fremd) {
    eigene.set(schluessel, name)
  }
  aendern({ ...bild, staende: { ...bild.staende, [schluessel]: s } })
  planeNachfrage()
}

/**
 * Die Seite der Konzernabschlüsse ist offen: Sie will auch die Läufe anderer
 * sehen (R1254). Der Rückgabewert schließt sie wieder.
 */
export function oeffneSeite(): () => void {
  seiten++
  void frageNach()
  return () => {
    seiten--
  }
}

/** Ob ein Lauf in diesem Browser angestoßen wurde — sonst sagt der Balken, wer (R1254). */
export function istEigen(schluessel: string): boolean {
  return eigene.has(schluessel)
}

/** Die Seite hat ein Ergebnis gezeigt. */
export function vergiss(ergebnis: Ergebnis): void {
  aendern({ ...bild, ergebnisse: bild.ergebnisse.filter((e) => e !== ergebnis) })
}

/** Nur für die Prüffälle: alles auf Anfang. */
export function setzeZurueck(): void {
  if (uhr !== null) {
    window.clearTimeout(uhr)
    uhr = null
  }
  eigene.clear()
  seiten = 0
  aendern({ staende: {}, ergebnisse: [] })
}
